import {
  Archive,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  RotateCcw,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActiveFilterChips, FiltersPopover } from '@/components/ui/list-filters';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { TagFilterSelect } from '@/features/tags';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { BASE as COMPANIES_BASE } from '../api';
import { CompanyProfileFilters } from '../components/CompanyProfileFilters';
import {
  NO_PROFILE_FILTERS,
  profileFilterChips,
  profileFilterParams,
} from '../profileFilters';
import { useArchiveCompany, useCompanies, usePurgeCompany, useRestoreCompany } from '../queries';
import { useCompaniesUi, type CompaniesViewMode } from '../store/viewPrefs';
import type { Company } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type CompanyOrderingColumn = 'name' | 'created' | 'modified' | 'is_active';

const VIEW_MODES: readonly ViewModeOption<CompaniesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

export function CompaniesListPage() {
  const navigate = useNavigate();
  const view = useCompaniesUi((s) => s.view);
  const setView = useCompaniesUi((s) => s.setView);
  const showArchived = useCompaniesUi((s) => s.showArchived);
  const setShowArchived = useCompaniesUi((s) => s.setShowArchived);
  const ordering = useCompaniesUi((s) => s.ordering);
  const setOrdering = useCompaniesUi((s) => s.setOrdering);
  const tag = useCompaniesUi((s) => s.tag);
  const setTag = useCompaniesUi((s) => s.setTag);
  const profile = useCompaniesUi((s) => s.profile);
  const setProfile = useCompaniesUi((s) => s.setProfile);

  // Search stays transient (cleared on reload).
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCompanies({
    page,
    search: search || undefined,
    archived: showArchived ? true : undefined,
    ordering,
    tag: tag === 'all' ? undefined : Number(tag),
    ...profileFilterParams(profile),
  });
  const onProfileChange = (next: typeof profile) => {
    setPage(1);
    setProfile(next);
  };
  const profileChips = profileFilterChips(profile, onProfileChange);

  const archive = useArchiveCompany();
  const restore = useRestoreCompany();
  const purge = usePurgeCompany();

  const results = data?.results ?? [];
  const onArchive = (c: Company) => archive.mutate(c.id);
  const onRestore = (c: Company) => restore.mutate(c.id);
  const onPurge = (c: Company) => purge.mutate(c.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
        <Button onClick={() => navigate('/crm/companies/new')}>
          <Plus /> New company
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search companies…"
              className="pl-8"
              aria-label="Search companies"
            />
          </div>
          <TagFilterSelect
            value={tag}
            onChange={(v) => {
              setPage(1);
              setTag(v);
            }}
          />
          <FiltersPopover
            activeFilters={profileChips.length}
            onClear={() => onProfileChange(NO_PROFILE_FILTERS)}
            contentClassName="w-80"
          >
            <CompanyProfileFilters value={profile} onChange={onProfileChange} />
          </FiltersPopover>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived companies' : 'Show archived companies'}
            title={showArchived ? 'Showing archived companies' : 'Show archived companies'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `companies.csv` without leaving the SPA.
              void exportCsv(COMPANIES_BASE, {
                search: search || undefined,
                archived: showArchived ? true : undefined,
                ordering,
                tag: tag === 'all' ? undefined : Number(tag),
                ...profileFilterParams(profile),
                export: 'csv',
              });
            }}
            aria-label="Export companies as CSV"
            title="Export companies as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {profileChips.length ? (
        <ActiveFilterChips
          chips={profileChips}
          onResetAll={() => {
            setTag('all');
            onProfileChange(NO_PROFILE_FILTERS);
          }}
        />
      ) : null}

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError).message}
        </p>
      ) : isPending ? (
        <CompaniesSkeleton />
      ) : results.length === 0 ? (
        // Empty state is consistent across views: render the table chrome with
        // an inline "No companies found." row, so the page never collapses into
        // a tiny floating message regardless of the persisted view mode.
        <CompaniesTable
          companies={[]}
          onRowClick={() => undefined}
          emptyMessage={showArchived ? 'No archived companies.' : undefined}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <CompaniesTable
          companies={results}
          onRowClick={(c) => navigate(`/crm/companies/${c.public_id}`)}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <CompaniesList
          companies={results}
          onOpen={(c) => navigate(`/crm/companies/${c.public_id}`)}
        />
      ) : view === 'cards' ? (
        <CompaniesCards
          companies={results}
          onOpen={(c) => navigate(`/crm/companies/${c.public_id}`)}
        />
      ) : (
        <CompaniesGroupedByStatus
          companies={results}
          onOpen={(c) => navigate(`/crm/companies/${c.public_id}`)}
        />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'company' : 'companies'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  companies: Company[];
  onOpen: (c: Company) => void;
}

function statusVariant(isActive: boolean) {
  return isActive ? 'default' : 'secondary';
}

function statusLabel(isActive: boolean) {
  return isActive ? 'Active' : 'Inactive';
}

interface CompaniesTableProps {
  companies: Company[];
  onRowClick: (c: Company) => void;
  emptyMessage?: string;
  onArchive: (c: Company) => void;
  onRestore: (c: Company) => void;
  onPurge: (c: Company) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function CompaniesTable({
  companies,
  onRowClick,
  emptyMessage,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: CompaniesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<CompanyOrderingColumn>
              column="name"
              value={ordering}
              onChange={onOrderingChange}
            >
              Name
            </SortableTableHead>
            <TableHead className="hidden sm:table-cell">Website</TableHead>
            <SortableTableHead<CompanyOrderingColumn>
              column="is_active"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<CompanyOrderingColumn>
              column="created"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Created
            </SortableTableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                {emptyMessage ?? 'No companies found.'}
              </TableCell>
            </TableRow>
          ) : (
            companies.map((c) => (
              <TableRow
                key={c.id}
                className="cursor-pointer"
                onClick={() => onRowClick(c)}
              >
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {c.website || '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(c.is_active)}>
                    {statusLabel(c.is_active)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {new Date(c.created).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  {c.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${c.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(c);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={c.name} onPurge={() => onPurge(c)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${c.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(c);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CompaniesCards({ companies, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {companies.map((c) => (
        <Card
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(c);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{c.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={statusVariant(c.is_active)}>{statusLabel(c.is_active)}</Badge>
            {c.website ? (
              <a
                href={c.website}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-foreground truncate underline-offset-2 hover:underline"
              >
                {c.website}
              </a>
            ) : null}
            <span className="text-muted-foreground ml-auto">
              {new Date(c.created).toLocaleDateString()}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CompaniesList({ companies, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {companies.map((c) => (
        <li key={c.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(c)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{c.name}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {c.website || '—'}
            </span>
            <Badge variant={statusVariant(c.is_active)} className="shrink-0">
              {statusLabel(c.is_active)}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {new Date(c.created).toLocaleDateString()}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function CompaniesGroupedByStatus({ companies, onOpen }: ListProps) {
  // Bucket by is_active — two sections: Active then Inactive (skip empties).
  const { active, inactive } = useMemo(() => {
    const a: Company[] = [];
    const i: Company[] = [];
    for (const c of companies) (c.is_active ? a : i).push(c);
    return { active: a, inactive: i };
  }, [companies]);

  const groups: Array<{ key: string; label: string; bucket: Company[] }> = [
    { key: 'active', label: 'Active', bucket: active },
    { key: 'inactive', label: 'Inactive', bucket: inactive },
  ];

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`companies-status-${key}`}>
            <h2
              id={`companies-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <CompaniesCards companies={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function CompaniesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
